import { useState } from "react";
import { Box, Flex, Icon } from "@chakra-ui/react";
import { LuCheck, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { CDS } from "design-system";
import { NewHireJourney } from "models/v2/newHireJourney";
import {
  findNextMilestoneName,
  getStageEndDate,
  getStageStartDate,
  getStageStatus,
} from "features/Stage/helpers";
import DashboardCard from "pages/PeopleTeam/DashboardPage/DashboardCard";

const MILESTONES = [
  { key: "preboarding", label: "Pre-boarding" },
  { key: "week1", label: "Week 1" },
  { key: "week2", label: "Week 2" },
  { key: "month1", label: "Month 1" },
  { key: "month2", label: "Month 2" },
  { key: "month3", label: "Month 3" },
  { key: "afterMonth3", label: "3+ Months" },
  { key: "afterMonth6", label: "6+ Months" },
  { key: "afterMonth12", label: "Past 1 Year" },
] as const;

type StageStatus = "complete" | "current" | "upcoming";

function formatStageRange(journey: NewHireJourney, key: string) {
  const start = getStageStartDate(journey, key);
  if (key === "afterMonth12") return `From ${start.format("MMM D")}`;
  const end = getStageEndDate(journey, key);
  const startLabel = start.format("MMM D");
  const endLabel = start.month() === end.month() ? end.format("D") : end.format("MMM D");
  return `${startLabel} – ${endLabel}`;
}

function StageDot({ status }: { status: StageStatus }) {
  if (status === "complete") {
    return (
      <Flex
        w="20px"
        h="20px"
        borderRadius="full"
        bg="brand.500"
        align="center"
        justify="center"
        flexShrink={0}
        zIndex={1}
      >
        <Icon as={LuCheck} boxSize="12px" color="white" strokeWidth={3} />
      </Flex>
    );
  }

  if (status === "current") {
    return (
      <Flex
        w="20px"
        h="20px"
        borderRadius="full"
        border="2px solid"
        borderColor="brand.500"
        bg="white"
        align="center"
        justify="center"
        flexShrink={0}
        zIndex={1}
      >
        <Box w="8px" h="8px" borderRadius="full" bg="brand.500" />
      </Flex>
    );
  }

  return (
    <Box
      w="20px"
      h="20px"
      borderRadius="full"
      bg="gray.200"
      flexShrink={0}
      zIndex={1}
    />
  );
}

interface JourneyStageListProps {
  journey: NewHireJourney;
}

export default function JourneyStageList({ journey }: JourneyStageListProps) {
  const [expanded, setExpanded] = useState(false);

  if (!journey.startDate) return null;

  const statuses = MILESTONES.map((m) => ({ ...m, status: getStageStatus(journey, m.key) }));
  let currentIndex = statuses.findIndex((m) => m.status === "current");
  if (currentIndex === -1) {
    // Handle gaps between stages (e.g. Day 1, when no milestone is technically "current")
    const lastCompleteIndex = statuses.map((m) => m.status).lastIndexOf("complete");
    currentIndex = Math.min(lastCompleteIndex + 1, statuses.length - 1);
  }

  const current = statuses[currentIndex];
  const next = statuses[currentIndex + 1];
  const nextMilestoneLabel = findNextMilestoneName(journey, MILESTONES.map((m) => m.key), currentIndex) ?? "None 🎉";
  const nextStageLabel = next?.label ?? "None 🎉";

  return (
    <DashboardCard padding="none">
      <Box px={5} pt={4} pb={4}>
        <CDS.Text12 fontWeight="600" color="content.tertiary" letterSpacing="wider" textTransform="uppercase" mb={2}>
          Current Stage · {currentIndex + 1} of {statuses.length}
        </CDS.Text12>
        <CDS.Heading22 mb={4}>{current.label}</CDS.Heading22>
        <Flex align="stretch" borderTopWidth="0.5px" borderColor="gray.200" mx={-5} px={0} pt={3}>
          <Box flex="1" px={5}>
            <CDS.Text12 fontWeight="600" color="content.tertiary" letterSpacing="wider" textTransform="uppercase" mb={1}>
              Next Milestone
            </CDS.Text12>
            <CDS.Text12 fontWeight="600">{nextMilestoneLabel}</CDS.Text12>
          </Box>
          <Box borderLeftWidth="0.5px" borderColor="gray.200" />
          <Box flex="1" px={5}>
            <CDS.Text12 fontWeight="600" color="content.tertiary" letterSpacing="wider" textTransform="uppercase" mb={1}>
              Next Stage
            </CDS.Text12>
            <CDS.Text12 fontWeight="600">{nextStageLabel}</CDS.Text12>
          </Box>
        </Flex>
      </Box>

      <Box
        as="button"
        type="button"
        w="100%"
        py={3}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
        borderTopWidth="0.5px"
        borderColor="gray.200"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        _hover={{ bg: "gray.50" }}
        _focusVisible={{ outline: "2px solid", outlineColor: "brand.500", outlineOffset: "-2px" }}
        transition="background 0.15s ease"
      >
        <CDS.Text14 fontWeight="500" color="content.primary">
          {expanded ? "Hide all stages" : "View all stages"}
        </CDS.Text14>
        <Icon as={expanded ? LuChevronUp : LuChevronDown} boxSize="16px" color="content.secondary" />
      </Box>

      {expanded && (
        <Box px={5} pb={4} pt={2}>
          <Flex direction="column" position="relative">
            <Box
              position="absolute"
              left="9px"
              top="16px"
              bottom="16px"
              width="2px"
              bg="gray.200"
            />
            {statuses.map((m, i) => {
              const isCurrent = i === currentIndex;
              return (
                <Flex key={m.key} align="center" justify="space-between" py={2}>
                  <Flex align="center" gap={3}>
                    <StageDot status={m.status} />
                    <CDS.Text14
                      fontWeight={isCurrent ? "700" : "500"}
                      color={isCurrent ? "brand.500" : "content.primary"}
                    >
                      {m.label}
                    </CDS.Text14>
                  </Flex>
                  {m.status !== "upcoming" && (
                    <CDS.Text14
                      color={isCurrent ? "brand.500" : "content.tertiary"}
                      fontWeight={isCurrent ? "500" : "400"}
                    >
                      {formatStageRange(journey, m.key)}
                    </CDS.Text14>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}
    </DashboardCard>
  );
}
